<template>
  <div id="login" :class="[device ? 'bg-mobile' : 'bg']">
    <div v-if="!device" id="login-logo">
      <div class="logo" />
      <div class="title">
        <span>物联网平台</span>
        <span>AIoT Platform</span>
        <div class="Science"> {{ version }}</div>
      </div>
    </div>
    <template v-if="isOverseas">
      <el-button type="primary" round class="login-button" @click="fXiaoKeLogin">{{ language === 'es' ? 'Iniciar sesión' : language === 'en' ? 'Login' : '登录' }}</el-button>
    </template>
    <template v-else>
      <div class="login-panel">
        <div class="login-tabs">
          <button :class="{ active: activeLoginType === 'feishu' }" type="button" @click="switchLoginType('feishu')">飞书扫码登录</button>
          <button :class="{ active: activeLoginType === 'account' }" type="button" @click="switchLoginType('account')">账号密码登录</button>
        </div>
        <div class="login-content">
          <transition :name="loginTransitionName" mode="out-in" @after-enter="handleLoginPaneAfterEnter">
            <div v-if="activeLoginType === 'feishu'" id="login-qrcode" key="feishu" class="login-pane">
              <div class="feishu-qr-wrapper">
                <div class="feishu-title">{{ isSelectingFeishuTenant ? '请选择登录企业' : isFeishuEnv ? '飞书安全登录' : '飞书扫码安全登录' }}</div>
                <div v-if="isSelectingFeishuTenant" class="feishu-tenant-select">
                  <button
                    v-for="tenant in feishuTenants"
                    :key="tenant.key"
                    :class="{ active: pendingFeishuTenantKey === tenant.key }"
                    type="button"
                    @click="selectFeishuTenant(tenant.key)"
                  >
                    <span class="tenant-card-badge">{{ tenant.badge }}</span>
                    <span class="tenant-card-name">{{ tenant.name }}</span>
                    <span class="tenant-card-tip">飞书登录</span>
                  </button>
                </div>
                <template v-else-if="isFeishuEnv">
                  <button class="feishu-sso-button" type="button" :disabled="loading" @click="executeFeishuSSO">
                    {{ loading ? '登录中...' : '使用' + currentFeishuTenant.name + '登录' }}
                  </button>
                  <div class="feishu-current-tenant">
                    <span>当前：{{ currentFeishuTenant.name }}</span>
                    <button type="button" @click="showFeishuTenantSelector">切换企业</button>
                  </div>
                  <div v-if="ssoError" class="feishu-error">{{ ssoError }}</div>
                </template>
                <template v-else>
                  <div id="feishu-qrcode" class="feishu-qrcode" />
                  <div class="feishu-current-tenant">
                    <span>当前：{{ currentFeishuTenant.name }}</span>
                    <button type="button" @click="showFeishuTenantSelector">切换企业</button>
                  </div>
                  <div v-if="ssoError" class="feishu-error">{{ ssoError }}</div>
                </template>
              </div>
            </div>
            <el-form
              v-else
              key="account"
              ref="accountForm"
              class="account-login-form login-pane"
              :model="accountForm"
              :rules="accountRules"
              label-position="top"
              @submit.native.prevent
            >
              <el-form-item label="账号" prop="account">
                <el-input v-model.trim="accountForm.account" maxlength="20" clearable placeholder="请输入账号" />
              </el-form-item>
              <el-form-item label="密码" prop="password">
                <el-input v-model.trim="accountForm.password" maxlength="20" show-password placeholder="请输入密码" />
              </el-form-item>
              <el-form-item label="验证码" prop="captchaCode">
                <div class="captcha-row">
                  <el-input v-model.trim="accountForm.captchaCode" maxlength="4" clearable placeholder="请输入验证码" @keyup.enter.native="handleAccountLogin" />
                  <button class="captcha-image" type="button" @click="refreshCaptcha">
                    <img v-if="captchaImage" :src="captchaImage" alt="验证码">
                    <span v-else>刷新</span>
                  </button>
                </div>
              </el-form-item>
              <el-button
                class="account-login-button"
                :disabled="!canAccountLogin"
                :loading="accountLoading"
                @click="handleAccountLogin"
              >登 录</el-button>
            </el-form>
          </transition>
        </div>
      </div>
    </template>
    <p v-if="!device && !isOverseas" class="copyright"><a href="https://beian.miit.gov.cn" target="_blank">粤ICP备15033411号-7未经许可不得复制、转载或摘编，违者必究！ 版权所有©2021广州万孚生物技术股份有限公司</a></p>
  </div>
</template>

<script>
import { filterVersion } from '@/utils/index'
import { setToken } from '@/utils/token'
import { fxLogin, fsLogin } from '@/api/common'
import { accountLogin, getCaptcha } from '@/api/user'
import MobileDetect from 'mobile-detect'
import Cookie from 'js-cookie'
import { getRootDomain } from '@/utils/index'

const rootDomain = getRootDomain()
const feishuTenantStorageKey = 'aiot_feishu_tenant_key'
const defaultFeishuAppid = process.env.VUE_APP_FEISHU_APPID || 'cli_a976b8a0257adcb5'
const quantFeishuAppid = process.env.VUE_APP_FEISHU_QUANT_APPID || (['beta', 'development'].includes(process.env.VUE_APP_ENV) ? 'cli_aacb6ff550b89ccc' : '')

export default {
  name: 'Login',
  data() {
    return {
      ua: '',
      device: '',
      version: '',
      language: navigator.language,
      isOverseas: process.env.VUE_APP_MODE === 'overseas',
      wwLogin: null,
      qrMessageHandler: null,
      isFeishuEnv: false,
      loading: false,
      ssoError: '',
      activeLoginType: 'feishu',
      loginTransitionName: 'login-slide-left',
      selectedFeishuTenantKey: 'wondfo',
      pendingFeishuTenantKey: '',
      isSelectingFeishuTenant: true,
      feishuTenants: [
        { key: 'wondfo', name: '万孚企业', badge: '万', appid: defaultFeishuAppid },
        { key: 'quant', name: '定量企业', badge: '定', appid: quantFeishuAppid }
      ].filter(tenant => tenant.appid),
      accountLoading: false,
      accountForm: {
        account: '',
        password: '',
        captchaCode: ''
      },
      captchaId: '',
      captchaImage: '',
      accountRules: {
        account: [
          { required: true, message: '请输入账号', trigger: 'blur' },
          { max: 20, message: '账号最多20个字符', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { max: 20, message: '密码最多20个字符', trigger: 'blur' }
        ],
        captchaCode: [
          { required: true, message: '请输入验证码', trigger: 'blur' },
          { max: 4, message: '验证码最多4个字符', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    currentFeishuTenant() {
      return this.getFeishuTenantByKey(this.selectedFeishuTenantKey) || this.feishuTenants[0]
    },
    currentFeishuAppid() {
      return this.currentFeishuTenant ? this.currentFeishuTenant.appid : defaultFeishuAppid
    },
    canAccountLogin() {
      return Boolean(this.accountForm.account && this.accountForm.password && !this.accountLoading)
    }
  },
  created() {
    this.ua = new MobileDetect(window.navigator.userAgent)
    this.device = this.ua.mobile()
  },
  mounted() {
    this.version = filterVersion()
    if (process.env.VUE_APP_MODE === 'overseas') {
      if (!this.device) {
        this.initFXiaoKe()
      }
      return
    }
    this.isFeishuEnv = this.getIsFeishuEnv()
    if (this.$route.query.code) {
      this.syncFeishuTenantFromState(this.$route.query.state)
      this.loginFn(this.$route.query.code, this.$route.query.state)
      return
    }
    this.restoreFeishuTenant()
    this.initFeishu()
  },
  beforeDestroy() {
    // 企业微信登录已切换为飞书登录，保留原销毁逻辑备用。
    // if (this.wwLogin) {
    //   this.wwLogin.unmount()
    // }
    this.removeFeishuQrMessageHandler()
  },
  methods: {
    switchLoginType(type) {
      if (this.activeLoginType === type) return
      this.loginTransitionName = type === 'account' ? 'login-slide-left' : 'login-slide-right'
      this.activeLoginType = type
      if (type === 'feishu') {
        return
      }
      this.removeFeishuQrMessageHandler()
      if (!this.captchaId) {
        this.refreshCaptcha()
      }
    },
    handleLoginPaneAfterEnter() {
      if (this.activeLoginType === 'feishu') {
        this.initFeishu()
      }
    },
    initFXiaoKe() {
      if (this.$route.query.code) {
        this.fxLoginFn(this.$route.query.code)
      }
      if (this.$route.query.callback) {
        this.fXiaoKeLogin()
      }
    },
    fXiaoKeLogin() {
      if (this.$route.query.callback) {
        Cookie.set('callback', decodeURIComponent(this.$route.query.callback), { domain: rootDomain, secure: true, sameSite: 'lax' })
      }
      location.href = `https://open.fxiaoke.com/oauth2.0/authorize?responseType=code&appId=${process.env.VUE_APP_XIAOKE_APPID}&redirectUrl=${encodeURIComponent(process.env.VUE_APP_XIAOKE_REDIRECT)}&state=${new Date().getTime()}`
    },
    initFeishu() {
      this.isFeishuEnv = this.getIsFeishuEnv()
      if (this.isSelectingFeishuTenant) {
        this.loading = false
        this.ssoError = ''
        this.removeFeishuQrMessageHandler()
        this.clearFeishuQrCode()
        return
      }
      if (this.isFeishuEnv) {
        this.executeFeishuSSO()
      } else {
        this.$nextTick(() => {
          this.initFeishuQrLogin()
        })
      }
    },
    getIsFeishuEnv() {
      const userAgent = window.navigator.userAgent.toLowerCase()
      return userAgent.indexOf('lark') > -1 || userAgent.indexOf('feishu') > -1
    },
    removeFeishuQrMessageHandler() {
      if (this.qrMessageHandler) {
        window.removeEventListener('message', this.qrMessageHandler)
        this.qrMessageHandler = null
      }
    },
    clearFeishuQrCode() {
      const qrcode = document.getElementById('feishu-qrcode')
      if (qrcode) {
        qrcode.innerHTML = ''
      }
    },
    getFeishuTenantByKey(key) {
      return this.feishuTenants.find(tenant => tenant.key === key)
    },
    syncFeishuTenantFromState(state) {
      const tenant = this.getFeishuTenantByKey(state)
      if (tenant) {
        this.selectedFeishuTenantKey = tenant.key
        this.pendingFeishuTenantKey = ''
        this.isSelectingFeishuTenant = false
      }
    },
    restoreFeishuTenant() {
      const tenant = this.getFeishuTenantByKey(window.localStorage.getItem(feishuTenantStorageKey))
      if (tenant) {
        this.selectedFeishuTenantKey = tenant.key
        this.pendingFeishuTenantKey = ''
        this.isSelectingFeishuTenant = false
      }
    },
    selectFeishuTenant(key) {
      const tenant = this.getFeishuTenantByKey(key)
      if (!tenant) return
      this.pendingFeishuTenantKey = key
      this.selectedFeishuTenantKey = key
      this.isSelectingFeishuTenant = false
      this.loading = false
      this.ssoError = ''
      window.localStorage.setItem(feishuTenantStorageKey, key)
      this.removeFeishuQrMessageHandler()
      this.clearFeishuQrCode()
      if (this.activeLoginType === 'feishu') {
        this.$nextTick(() => {
          this.initFeishu()
        })
      }
    },
    showFeishuTenantSelector() {
      this.isSelectingFeishuTenant = true
      this.pendingFeishuTenantKey = ''
      this.loading = false
      this.ssoError = ''
      this.removeFeishuQrMessageHandler()
      this.clearFeishuQrCode()
    },
    getFeishuRedirectUri() {
      return process.env.VUE_APP_FEISHU_REDIRECT || process.env.VUE_APP_WECHAT_REDIRECT || `${window.location.origin}/login`
    },
    getFeishuGotoUrl() {
      const redirectUri = this.getFeishuRedirectUri()
      // state 保存企业标识，扫码回调后页面重新加载也能使用正确 appid 登录。
      return `https://passport.feishu.cn/suite/passport/oauth/authorize?client_id=${this.currentFeishuAppid}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&state=${encodeURIComponent(this.currentFeishuTenant.key)}`
    },
    initFeishuQrLogin() {
      if (!window.QRLogin) {
        this.ssoError = '飞书扫码组件加载失败，请刷新页面重试'
        return
      }
      this.ssoError = ''
      this.removeFeishuQrMessageHandler()
      this.clearFeishuQrCode()
      const gotoUrl = this.getFeishuGotoUrl()
      const qrLoginObj = window.QRLogin({
        id: 'feishu-qrcode',
        goto: gotoUrl,
        width: '260',
        height: '260',
        style: 'width:260px;height:260px;border:0px'
      })
      this.qrMessageHandler = (event) => {
        if (qrLoginObj.matchOrigin(event.origin) && qrLoginObj.matchData(event.data)) {
          const loginTmpCode = event.data.tmp_code || event.data
          this.removeFeishuQrMessageHandler()
          window.location.href = `${gotoUrl}&tmp_code=${loginTmpCode}`
        }
      }
      window.addEventListener('message', this.qrMessageHandler, false)
    },
    executeFeishuSSO() {
      if (this.loading) return
      this.loading = true
      this.ssoError = ''
      if (window.tt && window.tt.requestAuthCode) {
        window.tt.requestAuthCode({
          appId: this.currentFeishuAppid,
          success: (res) => {
            if (res.code) {
              this.loginFn(res.code, this.currentFeishuTenant.key)
            } else {
              this.loading = false
              this.ssoError = '飞书登录失败，未获取到授权码'
            }
          },
          fail: (err) => {
            console.error(err)
            this.loading = false
            this.ssoError = '飞书免登失败，请退出后重试'
          }
        })
      } else {
        this.loading = false
        this.ssoError = '未检测到飞书登录环境，请在飞书客户端中打开'
      }
    },
    // 企业微信登录已切换为飞书登录，原逻辑保留备用。
    // initWeCom() {
    //   const _this = this
    //   this.wwLogin = window.ww.createWWLoginPanel({
    //     el: '#login-qrcode',
    //     params: {
    //       login_type: 'CorpApp',
    //       appid: process.env.VUE_APP_WECHAT_APPID,
    //       agentid: process.env.VUE_APP_WECHAT_AGENTID,
    //       redirect_uri: process.env.VUE_APP_WECHAT_REDIRECT,
    //       state: new Date().getTime(),
    //       redirect_type: 'callback',
    //       panel_size: 'small',
    //       lang: 'zh'
    //     },
    //     onCheckWeComLogin({ isWeComLogin }) {
    //       console.log(isWeComLogin)
    //     },
    //     onLoginSuccess({ code }) {
    //       _this.loginFn(code)
    //     },
    //     onLoginFail(err) {
    //       console.log(err)
    //     },
    //     onOpenInWecom() {
    //       _this.$router.push({ name: 'home' })
    //     }
    //   })
    // },
    loginFn(code, state) {
      const tenant = this.getFeishuTenantByKey(state) || this.currentFeishuTenant
      fsLogin({
        code: code,
        appid: tenant.appid,
        state: state || ''
      }).then((response) => {
        setToken(response.token)
        this.$router.push({ name: 'home' })
      }).catch((error) => {
        console.error(error)
        this.loading = false
        this.ssoError = '飞书登录失败，请退出后重试或联系管理员'
        if (!this.isFeishuEnv) {
          this.$nextTick(() => {
            this.initFeishuQrLogin()
          })
        }
      })
    },
    refreshCaptcha() {
      getCaptcha().then((response) => {
        this.captchaId = response.captchaId || ''
        this.captchaImage = response.imageData || ''
        this.accountForm.captchaCode = ''
      }).catch((error) => {
        console.error(error)
      })
    },
    handleAccountLogin() {
      if (!this.canAccountLogin) return
      this.$refs.accountForm.validate((valid) => {
        if (!valid) return
        if (!this.captchaId) {
          this.$message.error('请刷新验证码后重试')
          return
        }
        this.accountLoading = true
        accountLogin({
          account: this.accountForm.account,
          password: this.accountForm.password,
          captchaId: this.captchaId,
          captchaCode: this.accountForm.captchaCode
        }).then((response) => {
          setToken(response.token)
          // 清除上一登录账号的缓存，确保路由守卫重新获取用户信息和权限数据。
          sessionStorage.clear()
          this.$router.push({ name: 'home' })
        }).catch((error) => {
          console.error(error)
          this.refreshCaptcha()
        }).finally(() => {
          this.accountLoading = false
        })
      })
    },
    fxLoginFn(code) {
      fxLogin({
        code: code,
        appid: process.env.VUE_APP_XIAOKE_APPID,
        state: ''
      }).then((response) => {
        setToken(response.token)
        const callback = Cookie.get('callback', { domain: rootDomain })
        if (callback) {
          location.href = callback
          Cookie.remove('callback', { domain: rootDomain })
        } else {
          this.$router.push({ name: 'home' })
        }
      }).catch((error) => {
        console.error(error)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
#login {
  height: 100%;
  background-size: cover;
  overflow: hidden;
  color: #fff;
}

.bg {
  background: #00101A url("../assets/bg1.jpg") no-repeat top center;
}

#login-logo {
  position: absolute;
  top: 50%;
  left: 12%;
  margin-top: -130px;

  .logo {
    width: 150px;
    height: 150px;
    background: url("../assets/logo1.png");
    background-size: cover;
    margin-bottom: 12px;
  }

  .title {
    width: 500px;
    opacity: 0.9;

    span:nth-child(1) {
      font-size: 54px;
      font-weight: 400;
      display: block;
      margin-bottom: 23px;
      letter-spacing: 6px;
    }

    span:nth-child(2) {
      font-size: 40px;
      font-weight: 200;
      letter-spacing: 3px;
    }

    .Science {
      width: 65px;
      height: 35px;
      position: absolute;
      left: 190px;
      top: 58px;
      background: #409eff;
      color: #fff;
      line-height: 35px;
      font-weight: 600;
      text-align: center;
    }
  }
}

.login-button {
  position: absolute;
  right: 16%;
  top: 50%;
  padding: 12px 36px;
}

.login-panel {
  position: absolute;
  right: 16%;
  top: 50%;
  width: 480px;
  padding: 14px;
  transform: translateY(-50%);
  color: #333;
  border: 1px solid rgba(64, 158, 255, 0.36);
  border-radius: 12px;
  background: linear-gradient(145deg, rgba(7, 32, 49, 0.9), rgba(2, 14, 24, 0.78));
  box-shadow: 0 22px 55px rgba(0, 8, 18, 0.46), inset 0 1px 0 rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(8px);
}

.login-tabs {
  display: flex;
  height: 52px;
  padding: 4px;
  overflow: hidden;
  background: rgba(3, 19, 31, 0.72);
  border: 1px solid rgba(128, 192, 255, 0.18);
  border-radius: 8px;

  button {
    flex: 1;
    padding: 0;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: rgba(255, 255, 255, 0.82);
    font-size: 16px;
    cursor: pointer;
    outline: none;
    transition: background 0.24s ease, color 0.24s ease, box-shadow 0.24s ease;

    &.active {
      background: linear-gradient(135deg, #53a8ff, #2486f2);
      color: #fff;
      font-weight: 500;
      box-shadow: 0 8px 18px rgba(64, 158, 255, 0.3);
    }
  }
}

.login-content {
  position: relative;
  height: 430px;
  margin-top: 12px;
  padding: 34px 40px 30px;
  overflow: hidden;
  box-sizing: border-box;
  background:
    linear-gradient(145deg, rgba(13, 43, 62, 0.72), rgba(6, 24, 38, 0.82)),
    rgba(5, 25, 39, 0.78);
  border: 1px solid rgba(128, 192, 255, 0.22);
  border-radius: 10px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.09), 0 16px 34px rgba(0, 10, 24, 0.28);
  backdrop-filter: blur(12px) saturate(125%);
}

.login-pane {
  width: 100%;
  height: 100%;
}

.login-slide-left-enter-active,
.login-slide-left-leave-active,
.login-slide-right-enter-active,
.login-slide-right-leave-active {
  transition: transform 0.28s ease, opacity 0.28s ease;
}

.login-slide-left-enter,
.login-slide-right-leave-to {
  opacity: 0;
  transform: translateX(36px);
}

.login-slide-left-leave-to,
.login-slide-right-enter {
  opacity: 0;
  transform: translateX(-36px);
}

#login-qrcode {
  width: 100%;
}

.feishu-qr-wrapper,
.sso-status {
  width: 100%;
  height: 100%;
  padding: 0 10px;
  box-sizing: border-box;
  background: transparent;
  border-radius: 0;
  box-shadow: none;
  color: #333;
  text-align: center;
}

.feishu-title {
  margin-bottom: 18px;
  color: rgba(255, 255, 255, 0.94);
  font-size: 18px;
  font-weight: 500;
}

.feishu-tenant-select {
  display: flex;
  justify-content: center;
  gap: 18px;
  width: 100%;
  max-width: 330px;
  margin: 54px auto 0;

  button {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 138px;
    height: 138px;
    padding: 0;
    overflow: hidden;
    border: 1px solid rgba(128, 192, 255, 0.32);
    border-radius: 8px;
    background:
      linear-gradient(145deg, rgba(20, 66, 92, 0.72), rgba(6, 28, 45, 0.86)),
      rgba(255, 255, 255, 0.04);
    color: rgba(255, 255, 255, 0.9);
    cursor: pointer;
    outline: none;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 14px 28px rgba(0, 10, 24, 0.22);
    transition: transform 0.2s ease, background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;

    &::before {
      content: "";
      position: absolute;
      inset: 0;
      border-radius: inherit;
      background: radial-gradient(circle at 50% 0%, rgba(83, 168, 255, 0.22), transparent 58%);
      opacity: 0;
      transition: opacity 0.2s ease;
    }

    &:hover,
    &:focus {
      border-color: rgba(83, 168, 255, 0.72);
      transform: translateY(-2px);
      box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.12), 0 18px 34px rgba(0, 10, 24, 0.3), 0 0 0 3px rgba(64, 158, 255, 0.1);

      &::before {
        opacity: 1;
      }
    }

    &.active {
      border-color: rgba(64, 158, 255, 0.88);
      background:
        linear-gradient(145deg, rgba(37, 114, 166, 0.74), rgba(11, 47, 78, 0.9)),
        rgba(64, 158, 255, 0.14);
      color: #fff;
      box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.16), 0 18px 34px rgba(0, 10, 24, 0.32), 0 0 0 3px rgba(64, 158, 255, 0.16);
    }
  }
}

.tenant-card-badge {
  position: relative;
  z-index: 1;
  width: 46px;
  height: 46px;
  margin-bottom: 14px;
  border: 1px solid rgba(128, 192, 255, 0.38);
  border-radius: 8px;
  background: rgba(64, 158, 255, 0.14);
  color: #d9ecff;
  font-size: 24px;
  font-weight: 600;
  line-height: 44px;
}

.tenant-card-name,
.tenant-card-tip {
  position: relative;
  z-index: 1;
}

.tenant-card-name {
  font-size: 16px;
  font-weight: 500;
}

.tenant-card-tip {
  margin-top: 8px;
  color: rgba(255, 255, 255, 0.54);
  font-size: 12px;
}

.feishu-qrcode {
  width: 260px;
  height: 260px;
  margin: 0 auto;
  background: #fff;
  border: 10px solid #fff;
  border-radius: 4px;
  box-sizing: content-box;
  box-shadow: 0 18px 34px rgba(0, 10, 24, 0.32), 0 0 0 1px rgba(64, 158, 255, 0.1);
}

.feishu-tip {
  margin-top: 16px;
  color: rgba(255, 255, 255, 0.62);
  font-size: 14px;
}

.feishu-current-tenant {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
  margin-top: 18px;
  color: rgba(255, 255, 255, 0.68);
  font-size: 14px;

  button {
    padding: 0;
    border: 0;
    background: transparent;
    color: #53a8ff;
    font-size: 14px;
    cursor: pointer;
    outline: none;

    &:hover {
      color: #8cc5ff;
    }
  }
}

.feishu-error {
  margin-top: 12px;
  color: #f56c6c;
  font-size: 13px;
  line-height: 18px;
}

.feishu-sso-button {
  display: block;
  width: 220px;
  height: 44px;
  margin: 88px auto 0;
  border: 0;
  border-radius: 6px;
  background: linear-gradient(90deg, #409eff, #2f86ee);
  color: #fff;
  font-size: 16px;
  cursor: pointer;
  box-shadow: 0 10px 22px rgba(64, 158, 255, 0.26);

  &:disabled {
    cursor: not-allowed;
    background: #a0cfff;
    box-shadow: none;
  }
}

.account-login-form {
  width: 100%;
  padding-top: 2px;

  ::v-deep .el-form-item {
    margin-bottom: 20px;
  }

  ::v-deep .el-form-item__label {
    padding: 0 0 8px;
    color: rgba(255, 255, 255, 0.9);
    font-size: 14px;
    font-weight: 500;
    line-height: 1;
    white-space: nowrap;
  }

  ::v-deep .el-input__inner {
    height: 44px;
    line-height: 44px;
    border-color: rgba(128, 192, 255, 0.22);
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }

  ::v-deep .el-input__inner:focus {
    border-color: #409eff;
    background: rgba(255, 255, 255, 0.12);
    box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.14);
  }

  ::v-deep .el-input__inner::placeholder {
    color: rgba(255, 255, 255, 0.45);
  }

  ::v-deep .el-form-item__error {
    padding-top: 5px;
  }
}

.captcha-row {
  display: flex;
  align-items: center;
  gap: 12px;

  ::v-deep .el-input {
    flex: 1;
    min-width: 0;
  }
}

.captcha-image {
  width: 112px;
  height: 44px;
  padding: 0;
  overflow: hidden;
  border: 1px solid rgba(128, 192, 255, 0.38);
  border-radius: 6px;
  background: #fff;
  color: #606266;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, color 0.2s ease;

  &:hover {
    border-color: #409eff;
    color: #409eff;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.12);
  }

  img {
    display: block;
    width: 100%;
    height: 100%;
    background: #fff;
    object-fit: contain;
  }
}

.account-login-button {
  display: block;
  width: 100%;
  height: 48px;
  margin: 8px auto 0;
  border: 0;
  border-radius: 6px;
  background: linear-gradient(90deg, #409eff, #2f86ee);
  color: #fff;
  font-size: 16px;
  box-shadow: 0 10px 22px rgba(64, 158, 255, 0.26);

  &:hover,
  &:focus {
    background: linear-gradient(90deg, #53a8ff, #409eff);
    color: #fff;
  }

  &.is-disabled,
  &.is-disabled:hover,
  &.is-disabled:focus {
    background: #a0cfff;
    box-shadow: none;
    color: #fff;
  }
}

.sso-status {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: #409eff;
}

.loading-icon {
  width: 30px;
  height: 30px;
  margin: 0 auto 15px;
  border: 3px solid #f3f3f3;
  border-top-color: #409eff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.bg-mobile {
  background: #00101A url("../assets/bg3.jpg") no-repeat top center;
}

.copyright {
  width: 100%;
  text-align: center;
  position: absolute;
  bottom: 0;
  font-size: 14px;
}

@media screen and (max-width: 768px) {
  .login-panel {
    right: 50%;
    width: calc(100% - 32px);
    max-width: 480px;
    padding: 10px;
    transform: translate(50%, -50%);
  }

  .login-content {
    height: 430px;
    padding: 26px 20px;
  }
}
</style>
